import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Calendar, User, MapPin } from 'lucide-react';
import { postsService } from '../../services/posts';
import { PostStatus } from '../../types/post';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const PostViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Buscar post
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['post', id],
    queryFn: () => postsService.getPostById(id!),
    enabled: !!id,
  });

  // Deletar post
  const deleteMutation = useMutation({
    mutationFn: (id: string) => postsService.deletePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      navigate('/posts');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao deletar post');
    },
  });

  const handleDelete = () => {
    if (window.confirm('Tem certeza que deseja deletar este post?')) {
      deleteMutation.mutate(id!);
    }
  };

  const getStatusBadge = (status: PostStatus) => {
    const statusMap = {
      [PostStatus.Draft]: { label: 'Rascunho', className: 'bg-yellow-100 text-yellow-800' },
      [PostStatus.Published]: { label: 'Publicado', className: 'bg-green-100 text-green-800' },
      [PostStatus.Archived]: { label: 'Arquivado', className: 'bg-gray-100 text-gray-800' },
    };
    
    const statusInfo = statusMap[status];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getAuthorTypeLabel = (authorType: number) => {
    const typeMap = {
      0: 'Cafeteria',
      1: 'Usuário',
      2: 'CoffeeIn',
    };
    return typeMap[authorType as keyof typeof typeMap] || 'Desconhecido';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar post: {error?.message || 'Post não encontrado'}</p>
        <button
          onClick={() => navigate('/posts')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Voltar para Lista
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/posts')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{post.title}</h1>
            <div className="flex items-center space-x-4 mt-2">
              {getStatusBadge(post.status)}
              <span className="text-sm text-gray-500">
                {getAuthorTypeLabel(post.authorType)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/posts/${post.id}/edit`)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Deletar
          </button>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          {/* Informações do Post */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div className="flex items-center text-sm text-gray-600">
                <User className="w-4 h-4 mr-2" />
                <span className="font-medium">Autor:</span>
                <span className="ml-2">{post.authorName || 'N/A'}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="w-4 h-4 mr-2" />
                <span className="font-medium">Cidade:</span>
                <span className="ml-2">{post.cityName || 'Global'}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                <span className="font-medium">Criado em:</span>
                <span className="ml-2">{formatDate(post.createdAt)}</span>
              </div>
              {post.updatedAt !== post.createdAt && (
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span className="font-medium">Atualizado em:</span>
                  <span className="ml-2">{formatDate(post.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Resumo */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Resumo</h3>
            <p className="text-gray-700 bg-gray-50 p-4 rounded-md">
              {post.summary}
            </p>
          </div>

          {/* Imagens */}
          {post.images && post.images.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Imagens</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {post.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Imagem ${index + 1}`}
                      className="w-full h-48 object-cover rounded-lg shadow-md"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                      <button
                        onClick={() => window.open(image, '_blank')}
                        className="opacity-0 group-hover:opacity-100 bg-white bg-opacity-90 px-3 py-1 rounded text-sm font-medium transition-opacity"
                      >
                        Ver em Tela Cheia
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conteúdo Completo */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Conteúdo Completo</h3>
            <div className="prose max-w-none">
              <div className="text-gray-700 whitespace-pre-wrap">
                {post.content}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostViewPage;
